import type { ReactNode } from 'react';
import React, { useRef, useState } from 'react';

import { truncate } from 'lodash';
import styled from 'styled-components';

import { AddCircleIcon } from 'src/app/components/icons/add-circle-icon';

import { PaperIcon } from '../../icons/paper-icon';
import { Button } from '../button';
import { RebrandImageUploaderHeader } from '../image-uploader/image-uploader-rebrand.styles';

import {
  FileUploadButton,
  FileUploaderMessage,
  FileUploaderContainer,
  IconFileUploaderContainer,
} from './file-uploader.styles';

import type { ButtonProps } from 'src/app/components/lib/button';

type FileUploaderProps = {
  buttonSize?: 'extra-small' | 'large' | 'medium' | 'small';
  buttonVariant?: ButtonProps['variant'];
  disabled?: boolean;
  fileTypesAllowed?: string;
  hideFileName?: boolean;
  id?: string;
  label: ReactNode;
  loading?: boolean;
  uploadFile: (file: File) => void;
};

const FileNameContainer = styled.div`
  grid-column: 3 / span 2;
  font-size: 13px;
`;

export function FileUploaderButton({
  buttonVariant = 'secondary',
  disabled,
  fileTypesAllowed,
  uploadFile,
  label,
  buttonSize = 'small',
  id = null,
  hideFileName = false,
  loading = false,
}: FileUploaderProps) {
  const [fileName, setFileName] = useState('');

  const fileUploadButton = useRef(null);

  const handleFileUpload: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    const bundle = event.target.files;
    if (bundle?.length < 1) {
      return;
    }

    const file = bundle[0];
    setFileName(truncate(file.name, { length: 60 }));

    uploadFile(file);
    event.target.value = ''; // needed for onChange to fire again if multiple attempts to uploaded e.g. corrected file
  };

  const handleFileUploadClick = () => {
    fileUploadButton.current.click();
  };

  return (
    <>
      <input
        accept={fileTypesAllowed}
        id='file'
        ref={fileUploadButton}
        style={{ display: 'none' }}
        type='file'
        onChange={handleFileUpload}
      />
      <Button
        buttonSize={buttonSize}
        disabled={loading || disabled}
        id={id}
        label={label}
        loading={loading}
        variant={buttonVariant}
        onClick={handleFileUploadClick}
      />
      {!hideFileName && <FileNameContainer>{fileName}</FileNameContainer>}
    </>
  );
}

type FileUploaderPromptProps = {
  buttonLabel: ReactNode;
  disabled?: boolean;
  header?: ReactNode;
  message: ReactNode;
  onClick: (value: any) => void;
};

export function FileUploaderPrompt({ disabled, header, message, buttonLabel, onClick }: FileUploaderPromptProps) {
  const StyledFileUploaderContainer = FileUploaderContainer;
  const StyledFileUploaderMessage = FileUploaderMessage;
  const StyledFileUploaderHeader = RebrandImageUploaderHeader;
  const StyledFileUploaderButton = FileUploadButton;

  return (
    <StyledFileUploaderContainer>
      {header && <StyledFileUploaderHeader hasHeader={Boolean(header)}>{header}</StyledFileUploaderHeader>}
      <StyledFileUploaderMessage hasHeader={Boolean(header)}>{message}</StyledFileUploaderMessage>
      <StyledFileUploaderButton disabled={disabled} type='button' onClick={onClick}>
        {buttonLabel}
      </StyledFileUploaderButton>
    </StyledFileUploaderContainer>
  );
}

type StyledFileUploaderProps = {
  disabled?: boolean;
  displayFileName?: boolean;
  // comma-separated string: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#accept
  fileTypesAllowed?: string;
  header?: ReactNode;
  label: 'Choose file' | 'Choose file(s)' | 'Upload file';
  maxWidth?: string;
  message: ReactNode;
  uploadFile: (file: File) => void;
  usePaperIcon?: boolean;
};

export function FileUploader({
  disabled,
  header,
  maxWidth,
  message,
  label,
  fileTypesAllowed,
  uploadFile,
  usePaperIcon = false,
  displayFileName = true,
}: StyledFileUploaderProps) {
  const StyledFileUploaderContainer = FileUploaderContainer;
  const StyledFileUploaderMessage = FileUploaderMessage;
  const StyledFileUploaderHeader = RebrandImageUploaderHeader;
  const StyledFileUploaderButton = FileUploadButton;

  const [fileName, setFileName] = useState('');

  const fileUploadButton = useRef(null);

  const handleFileUpload: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    const bundle = event.target.files;
    if (bundle?.length < 1) {
      return;
    }

    const file = bundle[0];
    setFileName(truncate(file.name, { length: 60 }));

    uploadFile(file);
  };

  const handleFileUploadClick = () => {
    fileUploadButton.current.click();
  };

  return (
    <StyledFileUploaderContainer $maxWidth={maxWidth}>
      {usePaperIcon && <PaperIcon />}
      {header && <StyledFileUploaderHeader hasHeader={Boolean(header)}>{header}</StyledFileUploaderHeader>}
      <StyledFileUploaderMessage hasHeader={Boolean(header) || usePaperIcon}>{message}</StyledFileUploaderMessage>
      <input
        accept={fileTypesAllowed}
        id='file'
        ref={fileUploadButton}
        style={{ display: 'none' }}
        type='file'
        onChange={handleFileUpload}
      />
      <StyledFileUploaderButton disabled={disabled} type='button' onClick={handleFileUploadClick}>
        {label}
      </StyledFileUploaderButton>
      {displayFileName && <FileNameContainer>{fileName}</FileNameContainer>}
    </StyledFileUploaderContainer>
  );
}

type IconFileUploaderProps = {
  disabled?: boolean;
  label: string;
  onClick: (value: any) => void;
};

// Displays an icon that can be clicked to upload a file in empty state
export function IconFileUploader({ disabled, onClick, label }: IconFileUploaderProps) {
  return (
    <IconFileUploaderContainer>
      <button
        data-testid={`icon-file-uploader-${label}`}
        disabled={disabled}
        style={{ backgroundColor: 'transparent' }}
        type='button'
        onClick={onClick}
      >
        <AddCircleIcon />
      </button>
    </IconFileUploaderContainer>
  );
}
